import React, { useState, useCallback, useRef } from 'react';
import InsightViewer, { useImage, useInteraction } from '@lunit/insight-viewer';
import { useViewport } from '@lunit/insight-viewer/viewport';

const SimpleDicomViewer = ({ 
  dicomFile, 
  patientInfo = {}, 
  studyInfo = {}, 
  onClose, 
  readOnly = false, 
  onError,
  onMetadataExtracted 
}) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const imageUrlRef = useRef(null);

  // Create blob URL from Radiology file
  React.useEffect(() => {
    if (dicomFile) {
      setLoading(true);
      setError(null);
      
      try {
        const blobUrl = URL.createObjectURL(dicomFile);
        const wadouri = `wadouri:${blobUrl}`;
        setImageUrl(wadouri);
        imageUrlRef.current = wadouri;
        setLoading(false);
      } catch (err) {
        console.error('Error creating Radiology URL:', err);
        setError('Failed to load Radiology file: ' + err.message);
        setLoading(false);
        if (onError) onError('Failed to load Radiology file: ' + err.message);
      }
    }

    // Cleanup blob URL on unmount
    return () => {
      if (imageUrlRef.current && imageUrlRef.current.startsWith('wadouri:blob:')) {
        URL.revokeObjectURL(imageUrlRef.current.replace('wadouri:', ''));
      }
    };
  }, [dicomFile, onError]);

  // Use Insight Viewer hooks
  const { image, loading: imageLoading, error: imageError } = useImage({ 
    wadouri: imageUrl 
  });

  const { interaction, setInteraction } = useInteraction({
    mouseWheel: 'scale', // Mouse wheel for zoom
    primaryDrag: 'pan',  // Left click drag for pan
  });

  const viewerRef = React.useRef(null);
  const { viewport, setViewport, resetViewport } = useViewport({ 
    image, 
    viewerRef 
  });

  // Handle image loading errors
  React.useEffect(() => {
    if (imageError) {
      setError('Failed to load Radiology image: ' + imageError.message);
      if (onError) onError('Failed to load Radiology image: ' + imageError.message);
    }
  }, [imageError, onError]);

  // Extract metadata when image loads
  React.useEffect(() => {
    if (image && onMetadataExtracted) {
      const metadata = {
        width: image.width,
        height: image.height,
        columns: image.columns,
        rows: image.rows,
        modality: image.data?.modality || 'Unknown',
        photometricInterpretation: image.photometricInterpretation,
        windowWidth: image.windowWidth,
        windowCenter: image.windowCenter,
      };
      onMetadataExtracted(metadata);
    }
  }, [image, onMetadataExtracted]);

  // Tool handlers
  const handlePanTool = useCallback(() => {
    setInteraction(prev => ({ ...prev, primaryDrag: 'pan' }));
  }, [setInteraction]);

  const handleAdjustTool = useCallback(() => {
    setInteraction(prev => ({ ...prev, primaryDrag: 'adjust' }));
  }, [setInteraction]);

  const handleZoomTool = useCallback(() => {
    setInteraction(prev => ({ ...prev, primaryDrag: 'scale' }));
  }, [setInteraction]);

  const handleReset = useCallback(() => {
    resetViewport();
  }, [resetViewport]);

  // Loading state
  if (loading || imageLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #e0e0e0',
            borderTop: '4px solid #1976d2',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <div>Loading Radiology image...</div>
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ 
        margin: '16px', 
        padding: '16px', 
        backgroundColor: '#ffebee', 
        border: '1px solid #f44336', 
        borderRadius: '8px', 
        color: '#d32f2f' 
      }}>
        <h3 style={{ margin: '0 0 8px 0', color: '#d32f2f' }}>Error Loading Radiology</h3>
        <p style={{ margin: 0 }}>{error}</p>
      </div>
    );
  }

  // No file provided
  if (!dicomFile || !imageUrl) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        backgroundColor: '#f5f5f5',
        border: '2px dashed #ccc',
        borderRadius: '8px',
        color: '#666'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📁</div>
          <div>No Radiology file provided</div>
        </div>
      </div>
    );
  }

  const viewerProps = {
    image,
    viewerRef,
    viewport,
    interaction,
    onViewportChange: setViewport,
  };

  return (
    <div style={{ padding: '16px', height: '100%' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '16px',
        paddingBottom: '16px',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <div>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '1.5rem', fontWeight: 'bold' }}>
            Radiology Viewer
          </h2>
          {image && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {image.data?.modality && (
                <span style={{ 
                  padding: '4px 8px', 
                  backgroundColor: '#e8f5e8', 
                  borderRadius: '16px', 
                  fontSize: '0.75rem' 
                }}>
                  Modality: {image.data.modality}
                </span>
              )}
            </div>
          )}
        </div>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: '16px' }}>
        {/* Main Viewer */}
        <div style={{ flex: '1' }}>
          <div style={{ 
            border: '1px solid #e0e0e0', 
            borderRadius: '8px',
            overflow: 'hidden',
            position: 'relative'
          }}>
            {/* Toolbar */}
            <div style={{ 
              position: 'absolute', 
              top: '8px', 
              left: '8px', 
              zIndex: 1000,
              backgroundColor: 'rgba(0,0,0,0.8)',
              borderRadius: '8px',
              padding: '8px',
              display: 'flex',
              gap: '8px'
            }}>
              <button 
                style={{ 
                  padding: '8px 12px', 
                  backgroundColor: interaction.primaryDrag === 'pan' ? '#1976d2' : 'transparent',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
                onClick={handlePanTool}
                title="Pan Tool"
              >
                🤚 Pan
              </button>
              <button 
                style={{ 
                  padding: '8px 12px', 
                  backgroundColor: interaction.primaryDrag === 'adjust' ? '#1976d2' : 'transparent',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
                onClick={handleAdjustTool}
                title="Window/Level Adjust"
              >
                🔧 W/L
              </button>
              <button 
                style={{ 
                  padding: '8px 12px', 
                  backgroundColor: interaction.primaryDrag === 'scale' ? '#1976d2' : 'transparent',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
                onClick={handleZoomTool}
                title="Zoom Tool"
              >
                🔍 Zoom
              </button>
              <button 
                style={{ 
                  padding: '8px 12px', 
                  backgroundColor: 'transparent',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
                onClick={handleReset}
                title="Reset View"
              >
                🔄 Reset
              </button>
            </div>

            {/* Viewer */}
            <div style={{ 
              width: '100%', 
              height: '600px', 
              backgroundColor: '#000',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <InsightViewer {...viewerProps} />
            </div>
          </div>
        </div>

        {/* Info Panel */}
        <div style={{ width: '300px' }}>
          <div style={{ 
            border: '1px solid #e0e0e0', 
            borderRadius: '8px', 
            padding: '16px',
            backgroundColor: '#fafafa'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem' }}>Image Information</h3>
            
            {image && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ fontSize: '0.875rem' }}>
                  <strong>Dimensions:</strong> {image.columns || image.width} × {image.rows || image.height}
                </div>
                {image.data?.modality && (
                  <div style={{ fontSize: '0.875rem' }}>
                    <strong>Modality:</strong> {image.data.modality}
                  </div>
                )}
                {image.photometricInterpretation && (
                  <div style={{ fontSize: '0.875rem' }}>
                    <strong>Photometric:</strong> {image.photometricInterpretation}
                  </div>
                )}
                {image.windowWidth && (
                  <div style={{ fontSize: '0.875rem' }}>
                    <strong>Window:</strong> {image.windowWidth}
                  </div>
                )}
                {image.windowCenter && (
                  <div style={{ fontSize: '0.875rem' }}>
                    <strong>Level:</strong> {image.windowCenter}
                  </div>
                )}
              </div>
            )}

            {/* Usage Instructions */}
            <div style={{ marginTop: '24px', padding: '12px', backgroundColor: '#e3f2fd', borderRadius: '4px' }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '0.875rem', color: '#1976d2' }}>
                Controls
              </h4>
              <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '0.75rem', color: '#666' }}>
                <li>Mouse wheel: Zoom in/out</li>
                <li>Left click + drag: Pan or adjust W/L</li>
                <li>Use toolbar to switch tools</li>
                <li>Reset button restores original view</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleDicomViewer; 